'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import { some } from 'underscore';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import FormFieldGroupList from 'FormsUI/components/editor/form/formFields/FormFieldGroupList';
import DropZoneTarget from 'FormsUI/components/editor/form/dropZone/DropZoneTarget';
import { addFormFieldGroup as addFormFieldGroupFromAction, addFormField as addFormFieldFromAction, removeFormField as removeFormFieldFromAction, moveFormFieldIntoNewFormFieldGroup as moveFormFieldIntoNewFormFieldGroupFromAction, moveFormFieldIntoExistingFormFieldGroup as moveFormFieldIntoExistingFormFieldGroupFromAction, addRichText as addRichTextFromAction, removeRichText as removeRichTextFromAction, reorderRichText as reorderRichTextFromAction, updateDraggedItem } from 'FormsUI/actions/FormDragDrop';
import { removeDependentField as removeDependentFieldFromAction } from 'FormsUI/actions/FormSmartOptions';
import { getFormFieldGroups, getQueuedFormFieldGroups } from 'FormsUI/selectors/FormDefinition';
import { getMainFormFieldGroups } from 'FormsUI/selectors/FormFieldsProperties';
import { getDraggedItem } from 'FormsUI/selectors/FormDragDrop';
import { OPEN_PROGRESSIVE_PROFILING_FIELDS_EVENT } from 'FormsUI/constants/Events';
import { SpecialFieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { scrollMainPanel } from 'FormsUI/utils/AnimationUtils';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { TOGGLE_QUEUED_PROGRESSIVE_FIELD_ACCORDION } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { CommentableArea } from 'collaboration-sidebar';
import QueuedFieldsCardWrapper from 'FormsUI/components/editor/form/formFields/QueuedFieldsCardWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QueuedFieldsPanelWrapper = styled.div.withConfig({
  displayName: "QueuedFieldsPanel__QueuedFieldsPanelWrapper"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["box-shadow:inset ", " 0 ", " 0 ", ";margin-top:", ";padding:", ";width:100%;background-color:", ";"], theme.space['25'], theme.space['100'], theme.color['fill-tertiary-pressed'], theme.space['600'], theme.space['300'], theme.color['fill-surface-default']);
});
class QueuedFieldsPanel extends PureComponent {
  constructor() {
    super();
    this.state = {
      isExpanded: false
    };
    this.handleExpandedChange = this.handleExpandedChange.bind(this);
    this.handleExpandedOpen = this.handleExpandedOpen.bind(this);
  }
  componentDidMount() {
    window.addEventListener(OPEN_PROGRESSIVE_PROFILING_FIELDS_EVENT, this.handleExpandedOpen);
  }
  componentWillUnmount() {
    window.removeEventListener(OPEN_PROGRESSIVE_PROFILING_FIELDS_EVENT, this.handleExpandedOpen);
  }
  getLangKey(path) {
    return `editor.form.queuedFields.${path}`;
  }
  handleExpandedOpen(event) {
    if (event.detail.verification && this.props.queuedFormFieldGroups.length > 0) {
      return;
    }
    this.setState({
      isExpanded: true
    }, () => {
      scrollMainPanel('[data-test-id="queued-fields-panel"]');
    });
  }
  handleExpandedChange() {
    this.setState({
      isExpanded: !this.state.isExpanded
    });
    trackEditorInteraction(TOGGLE_QUEUED_PROGRESSIVE_FIELD_ACCORDION);
  }
  hasProgressiveProfilingBehaviourOnMainFields() {
    return some(this.props.mainFormFieldGroups, group => some(group.fields, field => field.isSmartField));
  }
  maybeRenderQueuedFields() {
    const {
      mainFormFieldGroups,
      queuedFormFieldGroups,
      handleRichTextSelect,
      handleFormFieldSelect,
      selectedFormItemType,
      selectedFormItemReference
    } = this.props;
    if (queuedFormFieldGroups.length === 0 && this.hasProgressiveProfilingBehaviourOnMainFields()) {
      return this.renderNoQueuedFieldAlert();
    }
    const queuedFormFieldGroupsWithFullLength = mainFormFieldGroups.map(() => null) // just so we have the right indexes for the drop targets
    .concat(queuedFormFieldGroups);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        marginInline: "1000",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: this.getLangKey('descriptionABM')
        })
      }), /*#__PURE__*/_jsx(FormFieldGroupList, {
        formFieldGroups: queuedFormFieldGroupsWithFullLength,
        handleRichTextSelect: handleRichTextSelect,
        handleFormFieldSelect: handleFormFieldSelect,
        isQueued: true,
        selectedFormItemType: selectedFormItemType,
        selectedFormItemReference: selectedFormItemReference
      })]
    });
  }
  renderNoQueuedFieldAlert() {
    return /*#__PURE__*/_jsx(Alert, {
      type: "warning",
      marginBlockEnd: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'editor.preview.sidepanel.warningNoQueuedFields'
      })
    });
  }
  render() {
    const {
      queuedFormFieldGroups,
      addFormFieldGroup,
      moveFormFieldIntoNewFormFieldGroup,
      formFieldGroups,
      selectedFormItemReference
    } = this.props;
    const {
      isExpanded
    } = this.state;
    return /*#__PURE__*/_jsx(QueuedFieldsPanelWrapper, {
      "data-test-id": "queued-fields-panel",
      children: /*#__PURE__*/_jsx(CommentableArea, {
        moduleId: "queue-progressive-fields",
        verticalPositioning: true,
        description: {
          translationKey: 'formsQueuedProgressiveFields'
        },
        children: /*#__PURE__*/_jsx(QueuedFieldsCardWrapper, {
          isExpanded: isExpanded,
          handleExpandedChange: this.handleExpandedChange,
          queuedFormFieldGroups: queuedFormFieldGroups,
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [this.maybeRenderQueuedFields(), /*#__PURE__*/_jsx(DropZoneTarget, {
              addFormFieldGroup: addFormFieldGroup,
              moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroup,
              formFieldGroupIndex: formFieldGroups.length // append to end of all form field groups
              ,
              disabled: Boolean(selectedFormItemReference),
              isQueued: true
            })]
          })
        })
      })
    });
  }
}
QueuedFieldsPanel.propTypes = {
  formFieldGroups: PropTypes.array.isRequired,
  mainFormFieldGroups: PropTypes.array.isRequired,
  queuedFormFieldGroups: PropTypes.array.isRequired,
  selectedFormItemType: PropTypes.oneOf([...Object.values(FieldTypes || {}), ...Object.values(SpecialFieldTypes || {})]),
  selectedFormItemReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleFormFieldSelect: PropTypes.func.isRequired,
  handleRichTextSelect: PropTypes.func.isRequired,
  addFormFieldGroup: PropTypes.func.isRequired,
  moveFormFieldIntoNewFormFieldGroup: PropTypes.func.isRequired
};
const mapStateToProps = state => ({
  formFieldGroups: getFormFieldGroups(state),
  mainFormFieldGroups: getMainFormFieldGroups(state),
  queuedFormFieldGroups: getQueuedFormFieldGroups(state),
  draggedItem: getDraggedItem(state)
});
const actionCreators = {
  addFormFieldGroup: addFormFieldGroupFromAction,
  addFormField: addFormFieldFromAction,
  removeFormField: removeFormFieldFromAction,
  moveFormFieldIntoNewFormFieldGroup: moveFormFieldIntoNewFormFieldGroupFromAction,
  moveFormFieldIntoExistingFormFieldGroup: moveFormFieldIntoExistingFormFieldGroupFromAction,
  addRichText: addRichTextFromAction,
  removeRichText: removeRichTextFromAction,
  reorderRichText: reorderRichTextFromAction,
  updateDraggedItem,
  removeDependentField: removeDependentFieldFromAction
};
export default connect(mapStateToProps, actionCreators)(QueuedFieldsPanel);